// src/hooks/useBlog.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  addDoc,
  serverTimestamp,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../Firebase/Config';

/* ── Types ── */

export interface Paragraph {
  id: string;
  content: string;
  order: number;
}

export interface BlogPost {
  id: string;
  title: string;
  authorName: string;
  backgroundImageUrl: string;
  createdAt: any;
  paragraphs: Paragraph[];
  tags: string[];
  slug: string;
  status: string;
}

export interface Comment {
  id: string;
  name: string;
  email: string;
  content: string;
  createdAt: any;
}

export interface PaginatedBlogsResult {
  blogs: BlogPost[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

export const PAGE_SIZE = 2;

/* ── Query Keys ── */

export const BLOG_KEYS = {
  all: ['blogs'] as const,
  lists: () => ['blogs', 'list'] as const,
  page: (page: number) => ['blogs', 'list', page] as const,
  search: (term: string) => ['blogs', 'search', term] as const,
  detail: (slug: string) => ['blogs', 'detail', slug] as const,
  comments: (blogId: string) => ['blogs', 'comments', blogId] as const,
};

/* ── Fetchers ── */

async function fetchBlogsPage(
  page: number,
  cursors: (QueryDocumentSnapshot<DocumentData> | null)[]
): Promise<PaginatedBlogsResult> {
  const blogsRef = collection(db, 'blogs');

  const baseConstraints = [
    where('status', '==', 'published'),
    orderBy('createdAt', 'desc'),
    limit(PAGE_SIZE + 1),  // ← fetch one extra
  ];

  let q = query(blogsRef, ...baseConstraints);

  if (page > 1 && cursors[page - 2]) {
    q = query(
      blogsRef,
      where('status', '==', 'published'),
      orderBy('createdAt', 'desc'),
      startAfter(cursors[page - 2]!),
      limit(PAGE_SIZE + 1),  // ← fetch one extra
    );
  }

  const snapshot = await getDocs(q);
  const hasMore = snapshot.docs.length > PAGE_SIZE;  // ← real check
  const docs = hasMore ? snapshot.docs.slice(0, PAGE_SIZE) : snapshot.docs;  // ← trim the extra
  const blogs = docs.map(doc => ({ id: doc.id, ...doc.data() })) as BlogPost[];
  const cursor = docs.length > 0 ? docs[docs.length - 1] : null;

  return { blogs, cursor, hasMore };
}

async function fetchAllPublishedBlogs(): Promise<BlogPost[]> {
  const q = query(
    collection(db, 'blogs'),
    where('status', '==', 'published'),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as BlogPost[];
}

async function fetchBlogBySlug(slug: string): Promise<BlogPost | null> {
  const q = query(
    collection(db, 'blogs'),
    where('slug', '==', slug),
    where('status', '==', 'published')
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as BlogPost;
}

async function fetchComments(blogId: string): Promise<Comment[]> {
  const commentsRef = collection(db, 'blogs', blogId, 'comments');
  const q = query(commentsRef, orderBy('createdAt', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Comment[];
}

/* ── Hooks ── */

export const useBlogsPage = (
  page: number,
  cursors: (QueryDocumentSnapshot<DocumentData> | null)[]
) => {
  return useQuery({
    queryKey: BLOG_KEYS.page(page),
    queryFn: () => fetchBlogsPage(page, cursors),
    staleTime: 1000 * 60 * 5,
    placeholderData: (prev: any) => prev,
  });
};

export const useBlogSearch = (searchTerm: string, enabled: boolean) => {
  return useQuery({
    queryKey: BLOG_KEYS.search(searchTerm),
    queryFn: async () => {
      const all = await fetchAllPublishedBlogs();
      const lower = searchTerm.toLowerCase();
      return all.filter(
        blog =>
          blog.title.toLowerCase().includes(lower) ||
          blog.authorName.toLowerCase().includes(lower) ||
          blog.tags.some(tag => tag.toLowerCase().includes(lower)) ||
          blog.paragraphs.some(p => p.content.toLowerCase().includes(lower))
      );
    },
    enabled: enabled && searchTerm.trim().length > 0,
    staleTime: 1000 * 60 * 3,
  });
};

export const useBlogBySlug = (slug: string, initialBlog?: BlogPost | null) => {
  return useQuery({
    queryKey: BLOG_KEYS.detail(slug),
    queryFn: () => fetchBlogBySlug(slug),
    enabled: !initialBlog && !!slug,
    staleTime: 1000 * 60 * 10,
    initialData: initialBlog ?? undefined,
  });
};

export const useBlogComments = (blogId: string | undefined) => {
  return useQuery({
    queryKey: BLOG_KEYS.comments(blogId ?? ''),
    queryFn: () => fetchComments(blogId!),
    enabled: !!blogId,
    staleTime: 1000 * 60 * 2,
  });
};

export const usePostComment = (blogId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; email: string; content: string }) => {
      const commentsRef = collection(db, 'blogs', blogId, 'comments');
      await addDoc(commentsRef, {
        name: data.name.trim(),
        email: data.email.trim(),
        content: data.content.trim(),
        createdAt: serverTimestamp(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BLOG_KEYS.comments(blogId) });
    },
  });
};